import {
  BiddableListing,
  IBiddingSignUpData,
  IBiddingSignUpErrors,
  useGetBiddableListingData,
  useSignUpBidding,
  useUpdateOperationMode,
} from '@frontend/api';
import { useAuth } from '@frontend/auth';
import { Translate } from '@frontend/translation';
import { Button, InnerTypography, PageWrapper } from '@frontend/ui-elements';
import { CircularProgress, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  BiddableListingCardWrapper,
  HeadingWrapper,
  LoaderWrapper,
  PrivatePageLeftColumn,
  PrivatePageRightColumn,
  PrivatePageWrapper,
  PublicPageLeftColumn,
  PublicPageRightColumn,
  PublicPageWrapper,
  Separator,
  SeparatorWrapper,
  SubheadingWrapper,
} from './BiddableListingPage.css';
import useModal from '@customer-platform-shared/components/Modals/useModal/useModal';
import BiddableListingDetails from './BiddableListingDetails/BiddableListingDetails';
import BiddingForm from './BiddingForm/BiddingForm';
import BiddingOnboardingSteps from './BiddingOnboardingSteps/BiddingOnboardingSteps';
import CreateAdditionalRoleModal from '@customer-platform-shared/components/Modals/CreateAdditionalRoleModal/CreateAdditionalRoleModal';
import ErrorSection from '@customer-platform-shared/components/ErrorSection/ErrorSection';
import PlatformHeader from '@customer-platform-shared/components/PlatformHeader/PlatformHeader';
import SuccessModal from '@customer-platform-shared/components/Modals/SuccessModal/SuccessModal';
import { HEADER_VARIANTS } from '@customer-platform-shared/components/PlatformHeader/PlatformHeader.types';
import { PLATFORM_ROUTES } from '@frontend/routing';
import SignUpBiddingForm from './SignUpBiddingForm/SignUpBiddingForm';
import { SubmitHandler } from 'react-hook-form';
import FormType from '@customer-platform-shared/components/Forms/utils/FormType.types';
import InfoModal from '@customer-platform-shared/components/Modals/InfoModal/InfoModal';

export function BiddableListingPage() {
  const {
    isAuthenticated,
    isCurrentUserBuyer,
    isCurrentUserSeller,
    isCurrentOperationModeSeller,
    getCurrentUser,
    checkSession,
    setIsFirstTimeLogin,
  } = useAuth();
  const intl = useIntl();
  const location = useLocation();
  const { uuid: listingId } = useParams();
  const theme = useTheme();
  const [formErrors, setFormErrors] = useState({});

  const {
    openCreateAdditionalRoleModal,
    openSuccessModal,
    closeModal,
    isCreateAdditionalRoleModalOpen,
    isSuccessModalOpen,
    openInfoModal,
    isInfoModalOpen,
  } = useModal();

  const {
    isSuccess: isGetBiddableListingDataSuccess,
    isPending: isGetBiddableListingDataPending,
    data: getBiddableListingDataResponse,
    error: getBiddableListingDataError,
    refetch: refetchBiddableListingData,
  } = useGetBiddableListingData(listingId as string);

  const {
    mutate: updateOperationMode,
    isSuccess: isUpdateOperationModeSuccessful,
  } = useUpdateOperationMode();

  const isBuyer = isAuthenticated && isCurrentUserBuyer;
  const isSeller =
    isAuthenticated && isCurrentUserSeller && isCurrentOperationModeSeller;
  const listing = getBiddableListingDataResponse?.data as BiddableListing;
  const navigate = useNavigate();

  useEffect(() => {
    if (isUpdateOperationModeSuccessful) {
      getCurrentUser && getCurrentUser();
      refetchBiddableListingData().then();
    }
  }, [
    getCurrentUser,
    isUpdateOperationModeSuccessful,
    refetchBiddableListingData,
  ]);

  const { mutate, isSuccess, isPending, error } = useSignUpBidding();

  const handleFormSubmit: SubmitHandler<
    FormType<IBiddingSignUpData>
  > = signUpSubmissionData => {
    mutate(signUpSubmissionData as IBiddingSignUpData);
  };

  useEffect(() => {
    if (isSuccess) {
      openSuccessModal();
    }
  }, [isSuccess, openSuccessModal]);

  useEffect(() => {
    if (error?.response?.data) {
      const errorData = error.response.data;
      // Handle different error structures
      if (typeof errorData === 'object' && errorData !== null) {
        const flattenedErrors = Object.values(errorData)
          .filter(Boolean)
          .reduce((acc, curr) => {
            if (typeof curr === 'object' && curr !== null) {
              return Object.assign(acc, curr);
            }
            return acc;
          }, {});
        setFormErrors(flattenedErrors);
      }
    }
  }, [error]);

  useEffect(() => {
    if (
      error &&
      // IDE might alert about data never being a string, but it can be in some cases
      (!error?.response?.data || typeof error?.response?.data === 'string')
    ) {
      openInfoModal();
    }
  }, [openInfoModal, error]);

  const refreshSession = () => {
    setIsFirstTimeLogin(true);
    closeModal();
    if (checkSession) checkSession();
  };

  return (
    <>
      <PlatformHeader
        variant={
          isBuyer
            ? HEADER_VARIANTS.BUYER
            : isSeller
              ? HEADER_VARIANTS.SELLER
              : HEADER_VARIANTS.GUEST
        }
      />
      <PageWrapper>
        <>
          {isGetBiddableListingDataSuccess &&
            (isBuyer ? (
              <PrivatePageWrapper>
                <PrivatePageLeftColumn>
                  <Typography variant="h4">
                    <InnerTypography
                      message={intl.formatMessage({
                        id:
                          (listing?.bidsExist ?? false)
                            ? 'customer-platform.biddable-listing-page.form-title.bid-again'
                            : 'customer-platform.biddable-listing-page.form-title',
                      })}
                      variant="h4"
                      color={theme.palette.primary[500]}
                    />
                  </Typography>

                  <BiddingForm
                    uuid={listingId as string}
                    onFormSuccess={refetchBiddableListingData}
                  />
                </PrivatePageLeftColumn>
                <PrivatePageRightColumn>
                  <BiddableListingCardWrapper>
                    <BiddableListingDetails {...listing} />
                  </BiddableListingCardWrapper>
                  <BiddingOnboardingSteps />
                </PrivatePageRightColumn>
              </PrivatePageWrapper>
            ) : isSeller ? (
              <>
                <ErrorSection
                  id="bidding-error-page"
                  title={intl.formatMessage({
                    id: 'customer-platform.biddable-listing-page.seller-error.title',
                  })}
                  description={intl.formatMessage({
                    id: 'customer-platform.biddable-listing-page.seller-error.description',
                  })}
                  btnText={intl.formatMessage({
                    id: 'customer-platform.biddable-listing-page.seller-error.button',
                  })}
                  onClick={() => openCreateAdditionalRoleModal()}
                  hideButtonIcon
                />
                {isCreateAdditionalRoleModalOpen && (
                  <CreateAdditionalRoleModal
                    isOpen={true}
                    onClose={closeModal}
                    onAccountUpdated={updateOperationMode}
                    newRoleName={intl.formatMessage({
                      id: isCurrentUserSeller
                        ? 'customer-platform.shared.company.role.buyer.name'
                        : 'customer-platform.shared.company.role.seller.name',
                    })}
                  />
                )}
              </>
            ) : (
              <PublicPageWrapper>
                <PublicPageLeftColumn>
                  <HeadingWrapper>
                    <Typography variant="h2">
                      <InnerTypography
                        message={intl.formatMessage({
                          id: 'customer-platform.biddable-listing-page.title',
                        })}
                        variant="h2"
                        color={theme.palette.primary[500]}
                      />
                    </Typography>
                  </HeadingWrapper>

                  <SignUpBiddingForm
                    errors={formErrors as IBiddingSignUpErrors}
                    uuid={listingId as string}
                    isSuccess={isSuccess}
                    isPending={isPending}
                    onSubmit={handleFormSubmit}
                  />

                  <SeparatorWrapper>
                    <Separator />
                    <Typography variant="p1">
                      <Translate id="customer-platform.biddable-listing-page.separator" />
                    </Typography>
                    <Separator />
                  </SeparatorWrapper>
                  <SubheadingWrapper>
                    <Typography variant="p1">
                      <Translate id="customer-platform.biddable-listing-page.subtitle.text" />
                    </Typography>

                    <Button
                      buttonType="secondary"
                      id="biddable-listing-page.button.open-login"
                      size="large"
                      onClick={() => {
                        navigate({
                          pathname: `${PLATFORM_ROUTES.login}`,
                          search: `sourceUrl=${location.pathname}&redirectUrl=${location.pathname}`,
                        });
                      }}
                    >
                      <Translate id="customer-platform.biddable-listing-page.subtitle.button.open-login" />
                    </Button>
                  </SubheadingWrapper>
                </PublicPageLeftColumn>
                <PublicPageRightColumn>
                  <BiddableListingCardWrapper>
                    <BiddableListingDetails {...listing} />
                  </BiddableListingCardWrapper>
                  <BiddingOnboardingSteps />
                </PublicPageRightColumn>
                {isSuccessModalOpen && (
                  <SuccessModal
                    title={intl.formatMessage({
                      id: 'customer-platform.biddable-listing-page.customer-form.success-modal.title',
                    })}
                    text={intl.formatMessage({
                      id: 'customer-platform.biddable-listing-page.customer-form.success-modal.text',
                    })}
                    onClose={closeModal}
                    primaryCta={{
                      text: intl.formatMessage({
                        id: 'customer-platform.biddable-listing-page.bidding-form.success-modal.cta.primary',
                      }),
                      onClick: () => {
                        refreshSession();
                      },
                    }}
                    secondaryCta={{
                      text: intl.formatMessage({
                        id: 'customer-platform.biddable-listing-page.bidding-form.success-modal.cta.secondary',
                      }),
                      onClick: () => {
                        window.location.href =
                          import.meta.env.VITE_STATIC_APP_URL;
                      },
                    }}
                  />
                )}
                {isInfoModalOpen && (
                  <InfoModal
                    title={intl.formatMessage({
                      id: 'customer-platform.shared.generic-error',
                    })}
                    primaryCta={{
                      text: intl.formatMessage({
                        id: 'customer-platform.shared.close',
                      }),
                      onClick: () => {
                        closeModal();
                      },
                    }}
                    severity="warning"
                  />
                )}
              </PublicPageWrapper>
            ))}

          {isGetBiddableListingDataPending && (
            <LoaderWrapper>
              <CircularProgress size={120} data-testid="CircularProgress" />
            </LoaderWrapper>
          )}

          {getBiddableListingDataError?.response?.status === 404 && (
            <ErrorSection
              title={intl.formatMessage({
                id: 'customer-platform.biddable-listing-page.error.title',
              })}
              description={intl.formatMessage({
                id: 'customer-platform.biddable-listing-page.error.description',
              })}
              btnText={intl.formatMessage({
                id: 'customer-platform.biddable-listing-page.error.button',
              })}
              onClick={() => {
                window.location.href = import.meta.env.VITE_STATIC_APP_URL;
              }}
            />
          )}
        </>
      </PageWrapper>
    </>
  );
}

export default BiddableListingPage;
